import type { ChatToolCall } from "./types";

interface PlanOption {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly recommended?: boolean;
}

interface PlanPayload {
  readonly kind: "plan_offered";
  readonly question: string;
  readonly rationale?: string;
  readonly options: ReadonlyArray<PlanOption>;
}

/**
 * Parses a propose_plan tool_result and renders interactive option
 * cards. Click → fires a new turn with the choice as the prompt.
 *
 * Returns null if the call isn't a propose_plan, the result hasn't
 * arrived yet, or the payload doesn't parse — caller can fall back to
 * the regular ToolCallRow render.
 */
export function PlanCard({
  call,
  onPick,
}: {
  call: ChatToolCall;
  onPick: (prompt: string) => void;
}) {
  if (call.name !== "propose_plan") return null;
  if (!call.result) {
    return (
      <PlanShell title="Preparing options…">
        <div style={{ color: "var(--text-tertiary)", fontSize: 12 }}>
          The agent is composing the choices.
        </div>
      </PlanShell>
    );
  }
  const payload = parsePayload(call.result);
  if (!payload) return null;

  return (
    <PlanShell title={payload.question}>
      {payload.rationale && (
        <div
          style={{
            color: "var(--text-secondary)",
            fontSize: 12.5,
            lineHeight: 1.55,
            marginBottom: 4,
          }}
        >
          {payload.rationale}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {payload.options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() =>
              onPick(
                `Use plan option "${opt.title}" (id: ${opt.id}). ${opt.description}`,
              )
            }
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 4,
              padding: "10px 12px",
              background: "var(--bg-panel-2)",
              border: opt.recommended
                ? "1px solid var(--accent-blue)"
                : "1px solid var(--border-default)",
              borderRadius: 8,
              color: "var(--text-primary)",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{opt.title}</span>
              {opt.recommended && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    background: "rgba(99,102,241,0.18)",
                    color: "#a5b4fc",
                    padding: "1px 6px",
                    borderRadius: 999,
                  }}
                >
                  Recommended
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>
              {opt.description}
            </div>
          </button>
        ))}
      </div>
    </PlanShell>
  );
}

function PlanShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: "1px solid var(--accent-blue)",
        background: "rgba(99,102,241,0.04)",
        borderRadius: 10,
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 16,
            height: 16,
            borderRadius: 4,
            background: "linear-gradient(135deg,#8B5CF6,#6366F1)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          ?
        </span>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
      </div>
      {children}
    </div>
  );
}

function parsePayload(raw: string): PlanPayload | null {
  try {
    const json = JSON.parse(raw) as unknown;
    if (
      !json ||
      typeof json !== "object" ||
      (json as { kind?: unknown }).kind !== "plan_offered"
    ) {
      return null;
    }
    const obj = json as Record<string, unknown>;
    if (typeof obj["question"] !== "string") return null;
    if (!Array.isArray(obj["options"])) return null;
    const options: PlanOption[] = [];
    for (const item of obj["options"]) {
      if (!item || typeof item !== "object") continue;
      const o = item as Record<string, unknown>;
      if (
        typeof o["id"] === "string" &&
        typeof o["title"] === "string" &&
        typeof o["description"] === "string"
      ) {
        options.push({
          id: o["id"],
          title: o["title"],
          description: o["description"],
          ...(typeof o["recommended"] === "boolean" && {
            recommended: o["recommended"],
          }),
        });
      }
    }
    if (options.length < 2) return null;
    return {
      kind: "plan_offered",
      question: obj["question"],
      ...(typeof obj["rationale"] === "string" && { rationale: obj["rationale"] }),
      options,
    };
  } catch {
    return null;
  }
}
